import { AxiomWithoutBatching } from "@axiomhq/js";
import { getCurrentDateISO } from "@pandas/core/dates";
import type {
	AnalyticsEventType,
	AnalyticsMetadata,
	AnalyticsValue,
	Environment,
	EventProperties,
	RegisteredProperties,
} from "../types";
import { BaseAnalyticsForwarder } from "./base-analytics";

export class AxiomAnalytics extends BaseAnalyticsForwarder {
	// Narrowed to the one method this class actually calls — lets a test inject
	// a fully-typed stub (`{ ingest: () => Promise.resolve() }`) through the
	// constructor seam below instead of reaching into the private field with a
	// cast (the repo bans `as unknown as`, tests included).
	private client: Pick<AxiomWithoutBatching, "ingest">;
	private dataset: string;
	private environment?: Environment;
	private project?: string;
	private pendingRequests: Promise<void>[] = [];

	constructor(
		token: string,
		orgId: string,
		dataset: string,
		environment?: Environment,
		project?: string,
		/** Test-only injection seam — production call sites always omit this and get a real `AxiomWithoutBatching`. */
		client?: Pick<AxiomWithoutBatching, "ingest">,
	) {
		super();
		this.client = client ?? new AxiomWithoutBatching({ token, orgId });
		this.dataset = dataset;
		this.environment = environment;
		this.project = project;

		if (!dataset || !orgId || !token) {
			console.error("Invalid Axiom parameters!");
		}
	}

	track<T extends AnalyticsEventType>(event: T, properties: EventProperties<T>, metadata?: AnalyticsMetadata): void {
		const eventName = this.formatEventName(event, properties as Record<string, AnalyticsValue>);
		const mergedProps = this.mergeProperties(properties, metadata);

		const propertiesToSnakeCase = this.transformPropertiesToSnakeCase(mergedProps as Record<string, AnalyticsValue>);

		const injectedProps = {
			source: "analytics",
			event: eventName,
			event_type: event,
			environment: this.environment,
			project: this.project,
			_time: getCurrentDateISO(),
			...propertiesToSnakeCase,
		};

		const request = this.client
			.ingest(this.dataset, injectedProps)
			.then(() => {})
			.catch((error) => {
				console.error("Failed to send analytics event to Axiom:", error);
			});
		this.trackPendingRequest(request);
	}

	identify(): void {
		const propertiesToSnakeCase = this.transformPropertiesToSnakeCase(this.registeredProperties);

		const injectedProps = {
			source: "analytics",
			event: "Identify",
			event_type: "Identify",
			environment: this.environment,
			project: this.project,
			_time: getCurrentDateISO(),
			...propertiesToSnakeCase,
		};

		const request = this.client
			.ingest(this.dataset, injectedProps)
			.then(() => {})
			.catch((error) => {
				console.error("Failed to send identify event to Axiom:", error);
			});
		this.trackPendingRequest(request);
	}

	async flush(): Promise<void> {
		await Promise.allSettled(this.pendingRequests);
		this.pendingRequests = [];
	}

	/** Diagnostic-only: the current in-flight request count (see {@link trackPendingRequest}). */
	get pendingRequestCount(): number {
		return this.pendingRequests.length;
	}

	/**
	 * Self-prunes `pendingRequests` on settle rather than only on an explicit `flush()` —
	 * a long-lived session (a kiosk tab, a grader console left open a shift) that never
	 * flushes would otherwise accumulate one settled promise per event forever. Bounds
	 * the array to whatever is genuinely still in flight; `flush()` itself is unaffected, since awaiting an already-settled promise resolves immediately either way.
	 */
	private trackPendingRequest(request: Promise<void>): void {
		this.pendingRequests.push(request);
		void request.finally(() => {
			const index = this.pendingRequests.indexOf(request);
			if (index !== -1) {
				this.pendingRequests.splice(index, 1);
			}
		});
	}

	register(properties: RegisteredProperties): void {
		this.registeredProperties = { ...this.registeredProperties, ...properties };
	}

	startSessionRecording(): void {}

	// Helper functions to follow ingestion conventions for Axiom
	private toSnakeCase(str: string): string {
		return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
	}

	private transformPropertiesToSnakeCase(obj: Record<string, AnalyticsValue>): Record<string, AnalyticsValue> {
		const result: Record<string, AnalyticsValue> = {};
		for (const key in obj) {
			// biome-ignore lint/suspicious/noPrototypeBuiltins: Vision AI targets eslint2021 which doesn't support Object.hasOwn, and this is a safe usage
			if (Object.prototype.hasOwnProperty.call(obj, key)) {
				result[this.toSnakeCase(key)] = obj[key];
			}
		}
		return result;
	}
}
